import { useCallback, useEffect, useRef, useState } from "react";
import { http, toastApiError, downloadUrl } from "../lib/api";
import { toast } from "../lib/notify";
import { FileText, Upload, Eye, Trash2, Paperclip, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PdfPreviewPanel from "./fin/PdfPreviewPanel";
import { OZLUK_KEYS } from "./personnel/ozlukKinds";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fmtDT = (s) => (s ? new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "");
// panelde gösterilebilen türler (pdf / png / jpg); mhtml-html yeni sekmede açılır
export const previewableDoc = (d) => ["application/pdf", "image/png", "image/jpeg"].includes(d.content_type) || /\.(pdf|png|jpe?g)$/i.test(d.original_filename || "");

const KIND_CLS = { hire: "bg-emerald-100 text-emerald-800", leave: "bg-red-100 text-red-800", izin: "bg-violet-100 text-violet-800" };

export async function uploadPersonnelDocument(personId, file, kind, note = "", refId = "") {
  const fd = new FormData();
  fd.append("file", file, file.name);
  const { data } = await http.post(`/personnel/${personId}/documents?kind=${encodeURIComponent(kind)}&note=${encodeURIComponent(note)}&ref_id=${encodeURIComponent(refId)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export default function PersonnelDocuments({ personId, refreshKey }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [kind, setKind] = useState("hire");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const load = useCallback(() => { if (personId) http.get(`/personnel/${personId}/documents`).then((r) => setDocs(r.data.filter((d) => !OZLUK_KEYS.includes(d.kind)))).catch(() => {}); }, [personId]);   // özlük belgeleri kendi sekmesinde
  useEffect(() => { load(); }, [load, refreshKey]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { await uploadPersonnelDocument(personId, f, kind); toast.success("Belge personel kartına eklendi"); load(); } catch (err) { toastApiError(err); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`"${d.original_filename}" silinsin mi?`)) return;
    try { await http.delete(`/personnel/documents/${d.id}`); toast.success("Belge silindi"); load(); } catch (err) { toastApiError(err); }
  };
  if (!personId) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="personnel-documents">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800 uppercase tracking-wide"><Paperclip className="w-3.5 h-3.5" /> Bildirge Arşivi <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px]" data-testid="personnel-documents-count">{docs.length}</span></span>
        <div className="ml-auto flex items-center gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="personnel-doc-kind" className="px-2 py-1 text-xs border border-slate-300 rounded-md">
            <option value="hire">İşe Giriş Bildirgesi</option><option value="leave">İşten Çıkış Bildirgesi</option><option value="izin">İzin Formu (imzalı)</option><option value="other">Diğer Belge</option>
          </select>
          <input ref={fileRef} type="file" accept=".mhtml,.mht,.png,.jpg,.jpeg,.pdf,.html" className="hidden" onChange={onPick} data-testid="personnel-doc-file" />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="personnel-doc-upload" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"><Upload className="w-3.5 h-3.5" /> {busy ? "Yükleniyor…" : "Belge Yükle"}</button>
        </div>
      </div>
      {docs.length === 0 ? (
        <div className="text-[11px] text-slate-400" data-testid="personnel-documents-empty">Henüz belge yok. SGK eklentisi onay sonrası bildirgeyi otomatik ekler; dilerseniz indirdiğiniz dosyayı da yükleyebilirsiniz.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`personnel-doc-${d.id}`}>
              <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${KIND_CLS[d.kind] || "bg-slate-100 text-slate-700"}`}>{d.kind_label}</span>
              <span className="truncate font-medium text-slate-800" title={d.original_filename}>{d.original_filename}</span>
              <span className="text-slate-400 whitespace-nowrap">{fmtSize(d.size)} · {fmtDT(d.created_at)}{d.created_by ? ` · ${d.created_by}` : ""}</span>
              {previewableDoc(d)
                ? <button type="button" onClick={() => setPreview({ path: `/personnel/documents/${d.id}/download`, name: d.original_filename, title: d.kind_label })} data-testid={`personnel-doc-view-${d.id}`} title="Panelde görüntüle (ESC kapatır)" className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"><Eye className="w-3 h-3" /> Görüntüle</button>
                : <a href={downloadUrl(`/personnel/documents/${d.id}/download`)} target="_blank" rel="noreferrer" data-testid={`personnel-doc-view-${d.id}`} title="Bu tür (mhtml/html) yeni sekmede açılır" className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"><ExternalLink className="w-3 h-3" /> Yeni sekmede aç</a>}
              {isAdmin && <button type="button" onClick={() => remove(d)} data-testid={`personnel-doc-delete-${d.id}`} className="p-1 rounded hover:bg-red-100 text-red-600" title="Sil"><Trash2 className="w-3.5 h-3.5" /></button>}
            </li>
          ))}
        </ul>
      )}
      <PdfPreviewPanel doc={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
